/**
 * P-38b (D-192, D-195, D-196) — the pure logic behind the meetings screens:
 * the form's rules and payloads, the calendar grid, paging and day keys.
 * No React, no i18n, no DOM: the unit tests import it directly.
 *
 * Day keys: a calendar day is always the LOCAL `YYYY-MM-DD` of the date,
 * never `toISOString().slice(0, 10)` (that is the UTC day, and a meeting at
 * 00:30 Israel time would land on the day before).
 */

export const MEETING_TYPES = ['pre_characterization', 'characterization_first', 'characterization_advanced', 'other'] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];

export const MEETING_PAGE_SIZE = 15;

const pad = (n: number) => String(n).padStart(2, '0');

/** The local `YYYY-MM-DD` of a date. */
export function localDayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The local day key of an ISO timestamp; '' when it is not a date. */
export function dayKeyOfIso(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : localDayKey(d);
}

/** The `datetime-local` input value (local time) of an ISO timestamp. */
export function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${localDayKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The day key of a `datetime-local` value (its date part, already local). */
export function dayKeyOfDatetimeLocal(v: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '';
}

/** `YYYY-MM-DD` → a Date at local noon (safe from DST edges). */
export function dateOfDayKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}

// ── The form ──────────────────────────────────────────────────────────────

/** FIX-D344 (D-344 §5): "איפה?" - zoom (made by the system) / office / phone / another link. '' = an older meeting (link). */
export const LOCATION_TYPES = ['zoom', 'office', 'phone', 'link'] as const;
export type LocationType = typeof LOCATION_TYPES[number];

export interface MeetingFormState {
  title: string;
  /** `YYYY-MM-DDTHH:mm`, local time. */
  dateTime: string;
  duration: number;
  description: string;
  meetingUrl: string;
  organizationId: string;
  leadId: string;
  meetingType: MeetingType | '';
  meetingTypeOther: string;
  aiAssist: boolean;
  /** OTE staff (user ids) who attend; the organizer may be among them. */
  userIds: string[];
  contactIds: string[];
  includePrimaryEmail: boolean;
  includeLead: boolean;
  /** FIX-D344: where the meeting is, and the address / phone number (office / phone). */
  locationType: LocationType | '';
  locationText: string;
}

export type MeetingFormErrorKey =
  | 'title' | 'dateTime' | 'duration' | 'meetingType' | 'customerXorLead';

/** Which fields are wrong (none: the form may be sent). */
export function validateMeetingForm(f: MeetingFormState): Partial<Record<MeetingFormErrorKey, true>> {
  const errors: Partial<Record<MeetingFormErrorKey, true>> = {};
  if (!f.title.trim()) errors.title = true;
  if (!f.dateTime) errors.dateTime = true;
  if (!(f.duration >= 5)) errors.duration = true;
  if (!f.meetingType) errors.meetingType = true;
  if (f.organizationId && f.leadId) errors.customerXorLead = true;
  return errors;
}

/**
 * Choosing a customer clears the lead and the other way round (D-192 §2: a
 * customer OR a lead, never both). The people picked for the old one go too.
 */
export function pickCustomer(f: MeetingFormState, organizationId: string): MeetingFormState {
  if (organizationId === f.organizationId) return f;
  return { ...f, organizationId, leadId: organizationId ? '' : f.leadId, contactIds: [], includePrimaryEmail: false, includeLead: false };
}

export function pickLead(f: MeetingFormState, leadId: string): MeetingFormState {
  if (leadId === f.leadId) return f;
  return { ...f, leadId, organizationId: leadId ? '' : f.organizationId, contactIds: [], includePrimaryEmail: false, includeLead: leadId ? true : false };
}

/**
 * D-196: an "internal only" employee is not offered in a customer / lead
 * meeting. When the context changes, anyone the new list does not offer is
 * dropped from the picked staff (the server refuses them anyway).
 */
export function reconcileStaff(selected: readonly string[], offered: readonly { user_id: string }[]): string[] {
  const ok = new Set(offered.map((s) => s.user_id));
  return selected.filter((id) => ok.has(id));
}

/** The people a meeting is for: kind + id, as the server's `participants` list. */
export type ParticipantPayload =
  | { kind: 'user'; user_id: string }
  | { kind: 'contact'; contact_id: string }
  | { kind: 'org_email' }
  | { kind: 'lead' };

/**
 * The participants list. The organizer is handled by `include_organizer`, so
 * they are left out of the user entries (on a create). On an edit the list is
 * the full list, the organizer stays on the server whatever is sent.
 */
export function buildParticipants(f: MeetingFormState, organizerId: string | null): ParticipantPayload[] {
  const out: ParticipantPayload[] = [];
  if (f.organizationId && f.includePrimaryEmail) out.push({ kind: 'org_email' });
  if (f.organizationId) for (const id of f.contactIds) out.push({ kind: 'contact', contact_id: id });
  if (f.leadId && f.includeLead) out.push({ kind: 'lead' });
  for (const id of f.userIds) if (id !== organizerId) out.push({ kind: 'user', user_id: id });
  return out;
}

interface WritePayload {
  title: string;
  meeting_datetime: string;
  duration_minutes: number;
  description: string | null;
  meeting_url: string | null;
  organization_id: string | null;
  lead_id: string | null;
  meeting_type: MeetingType;
  meeting_type_other: string | null;
  ai_assist: boolean;
  participants: ParticipantPayload[];
  location_type?: LocationType | null;
  location_text?: string | null;
}

/**
 * FIX-D344: the place. The link is sent only for "קישור אחר" (and an older meeting with no place): a Zoom link is made
 * by the system, office / phone have none.
 */
function locationPayload(f: MeetingFormState): Pick<WritePayload, 'location_type' | 'location_text'> & { meeting_url?: string | null } {
  if (!f.locationType) return {};
  const text = f.locationType === 'office' || f.locationType === 'phone' ? (f.locationText.trim() || null) : null;
  if (f.locationType === 'link') return { location_type: 'link', location_text: null };
  return { location_type: f.locationType, location_text: text, meeting_url: f.locationType === 'zoom' ? undefined : null };
}

function writePayload(f: MeetingFormState, organizerId: string | null): WritePayload {
  const loc = locationPayload(f);
  const out: WritePayload = {
    title: f.title.trim(),
    meeting_datetime: new Date(f.dateTime).toISOString(),
    duration_minutes: f.duration,
    description: f.description.trim() || null,
    meeting_url: f.meetingUrl.trim() || null,
    organization_id: f.organizationId || null,
    lead_id: f.leadId || null,
    meeting_type: (f.meetingType || 'other') as MeetingType,
    // "אחר" + free text is optional; the text is dropped for the other types.
    meeting_type_other: f.meetingType === 'other' ? (f.meetingTypeOther.trim() || null) : null,
    ai_assist: f.aiAssist,
    participants: buildParticipants(f, organizerId),
    ...loc,
  };
  // Zoom: the link is the system's (not sent at all).
  if ('meeting_url' in loc && loc.meeting_url === undefined) delete (out as Partial<WritePayload>).meeting_url;
  return out;
}

/** POST /meetings. `meId` is the creator; ticking them is `include_organizer`. */
export function buildCreatePayload(f: MeetingFormState, meId: string | null) {
  const p = writePayload(f, meId);
  return { ...p, include_organizer: !!meId && f.userIds.includes(meId) };
}

/** PATCH /meetings/:id — the full participants list (the server diffs it). */
export function buildUpdatePayload(f: MeetingFormState, organizerId: string | null) {
  return writePayload(f, organizerId);
}

export function emptyMeetingForm(init?: Partial<MeetingFormState>): MeetingFormState {
  return {
    title: '', dateTime: '', duration: 30, description: '', meetingUrl: '',
    organizationId: '', leadId: '', meetingType: '', meetingTypeOther: '', aiAssist: false,
    userIds: [], contactIds: [], includePrimaryEmail: false, includeLead: false,
    locationType: '', locationText: '',
    ...init,
  };
}

/** The shape of GET /meetings/:id the edit form reads. */
export interface MeetingDetailLike {
  title: string;
  description?: string | null;
  meeting_datetime: string;
  duration_minutes: number;
  meeting_url?: string | null;
  organization_id?: string | null;
  lead_id?: string | null;
  meeting_type?: string | null;
  meeting_type_other?: string | null;
  ai_assist?: boolean | null;
  organizer_id?: string | null;
  participants?: { kind: string; user_id?: string | null; contact_id?: string | null }[];
  location_type?: string | null;
  location_text?: string | null;
}

/** The edit form, filled from a meeting. */
export function formFromMeeting(m: MeetingDetailLike): MeetingFormState {
  const parts = m.participants ?? [];
  const type = (MEETING_TYPES as readonly string[]).includes(m.meeting_type ?? '') ? (m.meeting_type as MeetingType) : 'other';
  return emptyMeetingForm({
    title: m.title,
    dateTime: toDatetimeLocal(m.meeting_datetime),
    duration: m.duration_minutes,
    description: m.description ?? '',
    meetingUrl: m.meeting_url ?? '',
    locationType: (LOCATION_TYPES as readonly string[]).includes(m.location_type ?? '') ? (m.location_type as LocationType) : '',
    locationText: m.location_text ?? '',
    organizationId: m.organization_id ?? '',
    leadId: m.lead_id ?? '',
    meetingType: type,
    meetingTypeOther: m.meeting_type_other ?? '',
    aiAssist: !!m.ai_assist,
    userIds: parts.filter((p) => p.kind === 'user' && p.user_id).map((p) => p.user_id as string),
    contactIds: parts.filter((p) => p.kind === 'contact' && p.contact_id).map((p) => p.contact_id as string),
    includePrimaryEmail: parts.some((p) => p.kind === 'org_email'),
    includeLead: parts.some((p) => p.kind === 'lead'),
  });
}

// ── Lead picker ───────────────────────────────────────────────────────────

export interface LeadOptionLike {
  id: string;
  lead_status?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  email?: string | null;
}

/**
 * The lead picker offers the open leads. FIX-B17 (QA-P38b קל 2) — on an edit
 * of a meeting whose lead was converted or lost meanwhile, that lead is still
 * offered (first), so the select is not left empty.
 */
export function meetingLeadOptions<T extends LeadOptionLike>(all: T[], currentId?: string | null, fallback?: T | null): T[] {
  const open = all.filter((l) => l.lead_status !== 'converted' && l.lead_status !== 'lost');
  if (!currentId || open.some((l) => l.id === currentId)) return open;
  const cur = all.find((l) => l.id === currentId) ?? (fallback && fallback.id === currentId ? fallback : null);
  return cur ? [cur, ...open] : open;
}

// ── Conflicts ─────────────────────────────────────────────────────────────

/**
 * The staff ids the conflict check looks at: the picked staff and, always, the
 * organizer (D-192 §6 "כולל מי שיוצר"; FIX-B17 / QA-P38b קל 1 — even when the
 * organizer unticked themselves).
 */
export function conflictUserIds(f: MeetingFormState, organizerId?: string | null): string[] {
  return [...new Set(organizerId ? [...f.userIds, organizerId] : f.userIds)].sort();
}

/** The conflict request key; empty when there is nothing to ask (no date or nobody). */
export function conflictKey(f: MeetingFormState, excludeId?: string, organizerId?: string | null): string {
  const day = dayKeyOfDatetimeLocal(f.dateTime);
  const ids = conflictUserIds(f, organizerId);
  if (!day || ids.length === 0) return '';
  return `${day}|${ids.join(',')}|${excludeId ?? ''}`;
}

/**
 * "Continue anyway" applies to the conflict that was on screen. When the day
 * or the people change the new answer needs its own confirmation.
 */
export function conflictAcknowledged(ackKey: string, currentKey: string): boolean {
  return !!currentKey && ackKey === currentKey;
}

// ── Paging ────────────────────────────────────────────────────────────────

export interface MeetingsPageResult<T> {
  data?: T[];
  meta?: { total?: number; limit?: number; offset?: number };
}

/** The offset of the next page, or undefined when everything is loaded. */
export function nextPageOffset<T>(last: MeetingsPageResult<T> | undefined, loaded: number): number | undefined {
  if (!last?.data) return undefined;
  const total = last.meta?.total;
  if (typeof total === 'number') return loaded < total ? loaded : undefined;
  return last.data.length >= MEETING_PAGE_SIZE ? loaded : undefined;
}

/** All loaded pages as one list (a meeting that moved between pages shows once). */
export function flattenPages<T extends { id: string }>(pages: readonly MeetingsPageResult<T>[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const p of pages) for (const row of p.data ?? []) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

// ── Calendar ──────────────────────────────────────────────────────────────

export interface CalendarCell {
  /** Local `YYYY-MM-DD`. */
  key: string;
  date: Date;
  inMonth: boolean;
}

/** A month as weeks (Sunday first), padded with the days of the neighbour months. */
export function monthGrid(year: number, month: number): CalendarCell[][] {
  const first = new Date(year, month, 1, 12);
  const start = new Date(year, month, 1 - first.getDay(), 12);
  const weeks: CalendarCell[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w += 1) {
    const week: CalendarCell[] = [];
    for (let d = 0; d < 7; d += 1) {
      week.push({ key: localDayKey(cursor), date: new Date(cursor), inMonth: cursor.getMonth() === month });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
    // A sixth row that is all next month is dropped.
    if (w === 4 && cursor.getMonth() !== month) break;
  }
  return weeks;
}

/** The first and last instants of a month grid as ISO strings (for `from` / `to`). */
export function monthRangeIso(year: number, month: number): { from: string; to: string } {
  const grid = monthGrid(year, month);
  const first = grid[0][0].date;
  const last = grid[grid.length - 1][6].date;
  return {
    from: new Date(first.getFullYear(), first.getMonth(), first.getDate(), 0, 0, 0, 0).toISOString(),
    to: new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999).toISOString(),
  };
}

/** Meetings grouped by their local day, each day ordered by time. */
export function groupMeetingsByDay<T extends { meeting_datetime: string }>(rows: readonly T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const key = dayKeyOfIso(r.meeting_datetime);
    if (!key) continue;
    const list = map.get(key);
    if (list) list.push(r); else map.set(key, [r]);
  }
  for (const list of map.values()) list.sort((a, b) => new Date(a.meeting_datetime).getTime() - new Date(b.meeting_datetime).getTime());
  return map;
}

/** Previous / next month (month is 0-based). */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month + delta, 1, 12);
  return { year: d.getFullYear(), month: d.getMonth() };
}

// ── Who sees what ─────────────────────────────────────────────────────────

/** The status buttons of a meeting (complete, cancel, edit) are for those who may manage it. */
export function canShowManageActions(detail: { can_manage?: boolean; status?: string } | null | undefined): boolean {
  return !!detail?.can_manage && (detail.status === 'scheduled' || detail.status === 'rescheduled');
}

/** The RSVP column / statuses of the participants are shown only when the server sent them. */
export function participantStatusVisible(detail: { can_see_rsvp?: boolean } | null | undefined): boolean {
  return !!detail?.can_see_rsvp;
}

/**
 * D-232 sec.5 (QX-620): the answer badge of a participant row. The organizer always reads
 * "accepted" (even for someone without can_see_rsvp); the rest only when the server sent it.
 */
export function participantRsvpShown(p: { is_organizer?: boolean; rsvp_status?: string | null }): string | null {
  if (p.is_organizer) return 'accepted';
  return p.rsvp_status ?? null;
}

/** An edit changes what the invitees get: a new time or link needs a confirmation of the consequence. */
export function editNotifiesParticipants(
  before: { meeting_datetime: string; duration_minutes: number; meeting_url?: string | null },
  f: MeetingFormState,
): { time: boolean; link: boolean } {
  const time = !!f.dateTime && (new Date(f.dateTime).getTime() !== new Date(before.meeting_datetime).getTime() || f.duration !== before.duration_minutes);
  // FIX-D344: a Zoom meeting's link is the system's (an edit does not change it).
  const link = f.locationType === 'zoom' ? false : (f.meetingUrl.trim() || null) !== ((before.meeting_url ?? '').trim() || null);
  return { time, link };
}

/**
 * D-228 §1: what the "my answer" block shows on the single-meeting page.
 * - none: no block (not invited, organizer, meeting closed or already started)
 * - buttons: the three answer buttons (not answered yet, or the user chose "change answer")
 * - summary: one line with the current answer + a small "change answer" link
 */
export function myAnswerPanel(a: {
  hasParticipant: boolean;
  isOrganizer: boolean;
  open: boolean;
  startsAt: number;
  now: number;
  myAnswer: 'pending' | 'accepted' | 'declined' | 'tentative';
  changing: boolean;
}): 'none' | 'buttons' | 'summary' {
  if (!a.hasParticipant || a.isOrganizer || !a.open || a.now >= a.startsAt) return 'none';
  if (a.myAnswer === 'pending' || a.changing) return 'buttons';
  return 'summary';
}

/** D-228 §2: clicking the already-chosen answer, or clicking while a request is in flight, sends nothing. */
export function shouldSendRsvp(current: string | null | undefined, next: string, pending: boolean): boolean {
  return !pending && current !== next;
}
